import React, { ComponentProps, forwardRef, memo, ReactNode } from 'react'

import styled from '@emotion/styled'

import { isTransElement } from '@surglogs-app/util'

import { Button } from './Button'
import { FlexBox } from './FlexBox'
import { Icon } from './Icon'
import { Text, TextBold } from './Text'

type IconVariant = ComponentProps<typeof Icon>['variant']

const getIconVariant = (
  iconVariant?: IconVariant,
  active?: boolean,
  disabled?: boolean,
): IconVariant => {
  if (active) {
    return 'secondary'
  }

  if (disabled) {
    return 'navy.50'
  }

  return iconVariant
}

const StyledButton = styled(Button)<{ active?: boolean }>`
  white-space: normal;

  &:focus {
    box-shadow: none;
  }
`

interface InnerSidebarItemProps extends ComponentProps<typeof StyledButton> {
  description?: ReactNode
  iconVariant?: IconVariant
}

/** Designed for inner sidebars (chapters navigation, policies assignments, etc.) */
export const InnerSidebarItem = memo(
  forwardRef<HTMLButtonElement | null, InnerSidebarItemProps>(
    (
      {
        children,
        description,
        active,
        icon: IconComponent,
        iconVariant,
        disabled,
        ...rest
      },
      ref,
    ) => {
      return (
        <StyledButton
          {...{
            ref,
            w: '100%',
            h: 'unset',
            jc: 'flex-start',
            bd: 'none',
            py: 'abs.10',
            px: 'md',
            variant: active ? 'primary' : 'secondary',
            bgc: !active ? 'transparent' : undefined,
            active,
            disabled,
            'aria-selected': active,
            ...rest,
          }}>
          {IconComponent ? (
            <Icon
              {...{
                icon: IconComponent,
                variant: getIconVariant(iconVariant, active, disabled),
              }}
            />
          ) : null}
          <FlexBox {...{ fd: 'column', ta: 'left', gap: 'xs' }}>
            {typeof children === 'string' || isTransElement(children) ? (
              <TextBold {...{ color: 'inherit', size: 'xxs' }}>
                {children}
              </TextBold>
            ) : (
              children
            )}
            {description ? (
              <Text {...{ size: 'xxs', color: 'inherit' }}>{description}</Text>
            ) : null}
          </FlexBox>
        </StyledButton>
      )
    },
  ),
)
